// libraries
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
// styles
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import moment from 'moment'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ManageOrgSubscriptionBox from 'src/components/ManageOrgSubscriptionBox/ManageOrgSubscription'
import OrgMemberDetailsBox from 'src/components/OrgMemberDetailsBox/OrgMemberDetailsBox'
import OrgNameAndIndustryBox from 'src/components/OrgNameAndIndustryBox/OrgNameAndIndustryBox'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { SubscriptionStatus } from 'src/models/organizations.models'
import {
  useQueryOrganizationSaasTier,
  useQueryOrganizationSubscription,
} from 'src/queries/organizations/saas.queries'
import { getOrganization } from 'src/redux/actions/data/organizations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationIdFromRoute,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  selectAppName,
  selectIsExtendBrand,
} from 'src/redux/selectors/session.selectors'

import styles from './OrganizationInfo.style'

const OrganizationInfo: React.FC = () => {
  const user = useSelector(selectUserListModelFromState)
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)
  const isExtendBrand = useSelector(selectIsExtendBrand)

  const translate = useTranslate()

  const { data: organizationSubscription } = useQueryOrganizationSubscription({
    orgId: organization?.id,
    enabled: !!organization?.id,
  })

  const dispatch = useBackgroundDispatch()
  useEffect(() => {
    if (organizationId) {
      dispatch(getOrganization(organizationId, true))
    }
    // TODO - should not assume user exists in redux, so should load user here if iti doesn't exist
    // dispatch(getUser)
  }, [organizationId])

  const {
    data: currentPlan,
    isLoading: isLoadingCurrentPlan,
    isSuccess,
  } = useQueryOrganizationSaasTier({
    orgId: organization?.id || '',
    enabled: !!organization?.id,
  })

  let cancelsAt
  switch (organizationSubscription?.subscription?.state.status) {
    case SubscriptionStatus.NON_RENEWING:
      cancelsAt = organizationSubscription?.subscription?.state.cancelsAt
      break
    default:
      break
  }

  const { show_new_org_settings: showNewOrgSettings } = useSelector(
    selectFeatureFlaggerFromState,
  )

  if (user === undefined || organization === undefined) {
    return <LoadingBackgroundPage />
  }

  if (!showNewOrgSettings) {
    return (
      <>
        <Helmet>
          <title>{`Organization Info | ${appName}`}</title>
          <meta content={`Organization Info page of ${appName}`} />
        </Helmet>
        <BackgroundPageContent className="OrganizationInfo">
          {cancelsAt && (
            <AlertPanel variant="warning" icon={<AlertTriangleIcon />}>
              {translate('Your Pro subscription ends ${date}.', {
                date: moment(cancelsAt).format('MMMM D, YYYY'),
              })}
            </AlertPanel>
          )}
          <HorizontalContentCollection splitCenter>
            <OrgMemberDetailsBox user={user} organization={organization} />
            <OrgNameAndIndustryBox user={user} organization={organization} />
          </HorizontalContentCollection>
          {/* TODO: remove isSuccess check once we release saas b/c loading/error states are handled in the component */}
          {isExtendBrand && isSuccess && (
            <HorizontalContentCollection splitCenter>
              <ManageOrgSubscriptionBox
                user={user}
                organization={organization}
                currentPlan={currentPlan}
                isLoadingCurrentPlan={isLoadingCurrentPlan}
                isSuccess={isSuccess}
              />
              <div></div>
            </HorizontalContentCollection>
          )}
        </BackgroundPageContent>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Organization Info | ${appName}`}</title>
        <meta
          name="description"
          content={`Organization Info page of ${appName}`}
        />
      </Helmet>
      <LayoutShell
        subnavOptions={[]}
        subnavDesign={SubnavDesign.Floating}
        content={
          <BackgroundPageContent className="OrganizationInfo" css={styles}>
            <HorizontalContentCollection splitCenter>
              <OrgNameAndIndustryBox user={user} organization={organization} />
              <OrgMemberDetailsBox user={user} organization={organization} />
            </HorizontalContentCollection>
          </BackgroundPageContent>
        }
        title={<span>{translate('Organization Info')}</span>}
        buttons={[]}
      />
    </>
  )
}

export default OrganizationInfo
